import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { formatAddress } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    MessageCircle, 
    Send, 
    ChevronLeft,
    Circle,
    Loader2,
    Crown,
    Wifi,
    WifiOff
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';

function ChatMessage({ message, isOwn, showSender }) {
    const time = message.created_at ? new Date(message.created_at) : new Date();
    return (
        <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
            <div className={`max-w-[70%]`}>
                {showSender && !isOwn && (
                    <div className="text-xs text-white/40 mb-1 ml-3">
                        {formatAddress(message.sender)}
                    </div>
                )}
                <div className={`px-4 py-2 rounded-2xl ${
                    isOwn 
                        ? 'bg-[#00FF66] text-[#0A0A0C] rounded-br-md' 
                        : 'bg-white/10 text-white rounded-bl-md'
                }`}>
                    <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                </div>
                <div className={`text-xs text-white/30 mt-1 ${isOwn ? 'text-right mr-2' : 'ml-2'}`}>
                    {format(time, 'HH:mm')}
                </div>
            </div>
        </div>
    );
}

function DateSeparator({ date }) {
    const d = new Date(date);
    let label = format(d, 'MMMM d, yyyy');
    if (isToday(d)) label = "Today";
    else if (isYesterday(d)) label = 'Yesterday';
    return (
        <div className="flex items-center justify-center my-4">
            <div className="bg-white/10 text-white/50 text-xs px-3 py-1 rounded-full">{label}</div>
        </div>
    );
}

function OnlineMembers({ members, teamMembers, captain }) {
    return (
        <div className="space-y-2">
            <h4 className="text-sm text-white/50 font-medium mb-3">Members</h4>
            {teamMembers.map(member => {
                const isOnline = members.includes(member);
                const isCaptain = member === captain;
                return (
                    <div key={member} className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5">
                        <Circle className={`w-2 h-2 ${isOnline ? 'fill-[#00FF66] text-[#00FF66]' : 'fill-white/20 text-white/20'}`} />
                        <span className="text-white/80 text-sm font-mono truncate">{formatAddress(member)}</span>
                        {isCaptain && <Crown className="w-3 h-3 text-[#FFD700] ml-auto" />}
                    </div>
                );
            })}
        </div>
    );
}

export function TeamChatPage() {
    const { teamId } = useParams();
    const { address, isConnected } = useWeb3();
    const [team, setTeam] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [onlineMembers, setOnlineMembers] = useState([]);
    const [wsConnected, setWsConnected] = useState(false);
    const [typingUser, setTypingUser] = useState(null);
    const messagesEndRef = useRef(null);
    const wsRef = useRef(null);
    const typingTimeout = useRef(null);
    
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Fetch team info
    useEffect(() => {
        if (teamId) {
            fetch(`${API_URL}/api/teams/${teamId}`).then(r => r.ok ? r.json() : null).then(d => d && setTeam(d)).catch(() => {});
        }
    }, [teamId]);

    // Fetch initial messages
    useEffect(() => {
        if (teamId && address) {
            setLoading(true);
            fetch(`${API_URL}/api/teams/${teamId}/chat/messages?address=${address}&limit=100`)
                .then(r => r.ok ? r.json() : { messages: [] })
                .then(d => setMessages(d.messages || []))
                .catch(() => {})
                .finally(() => setLoading(false));
            fetch(`${API_URL}/api/teams/${teamId}/chat/online`)
                .then(r => r.ok ? r.json() : { online_members: [] })
                .then(d => setOnlineMembers(d.online_members || []))
                .catch(() => {});
        }
    }, [teamId, address]);

    // WebSocket connection
    useEffect(() => {
        if (!teamId || !address || !isConnected) return;

        let ws;
        let reconnectTimer;
        
        const connect = () => {
            ws = new WebSocket(WS_URL);
            wsRef.current = ws;

            ws.onopen = () => {
                ws.send(JSON.stringify({ type: 'auth', address }));
                ws.send(JSON.stringify({ type: 'chat_join', team_id: teamId }));
                setWsConnected(true);
            };

            ws.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === 'new_message') {
                        setMessages(prev => {
                            if (prev.some(m => m.id === msg.message.id)) return prev;
                            return [...prev, { ...msg.message, is_mine: msg.message.sender === address?.toLowerCase() }];
                        });
                    } else if (msg.type === 'user_online') {
                        setOnlineMembers(prev => prev.includes(msg.address) ? prev : [...prev, msg.address]);
                    } else if (msg.type === 'user_offline') {
                        setOnlineMembers(prev => prev.filter(a => a !== msg.address));
                    } else if (msg.type === 'user_typing') {
                        setTypingUser(msg.address);
                        clearTimeout(typingTimeout.current);
                        typingTimeout.current = setTimeout(() => setTypingUser(null), 2000);
                    }
                } catch (parseErr) {
                    console.warn('Team chat WS message parse failed:', parseErr?.message);
                }
            };

            ws.onclose = () => {
                setWsConnected(false);
                reconnectTimer = setTimeout(connect, 3000);
            };

            ws.onerror = () => ws.close();
        };

        connect();

        return () => {
            clearTimeout(reconnectTimer);
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [teamId, address, isConnected]);

    useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !address) return;
        
        const content = newMessage.trim();
        setSending(true);

        // Try WebSocket first
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'chat_message', team_id: teamId, content }));
            // Optimistic update
            setMessages(prev => [...prev, {
                id: `temp-${Date.now()}`,
                sender: address.toLowerCase(),
                content,
                created_at: new Date().toISOString(),
                is_mine: true
            }]);
            setNewMessage('');
            setSending(false);
        } else {
            // Fallback to REST
            try {
                const response = await fetch(
                    `${API_URL}/api/teams/${teamId}/chat/messages?address=${address}&content=${encodeURIComponent(content)}`,
                    { method: 'POST' }
                );
                const result = await response.json();
                if (result.success) {
                    setNewMessage('');
                    setMessages(prev => [...prev, {
                        id: result.message_id,
                        sender: address.toLowerCase(),
                        content,
                        created_at: new Date().toISOString(),
                        is_mine: true
                    }]);
                } else {
                    toast.error(result.error || 'Send error');
                }
            } catch (error) {
                toast.error('Send error');
            } finally {
                setSending(false);
            }
        }
    };

    const handleTyping = () => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'chat_typing', team_id: teamId }));
        }
    };

    const isMember = team?.members?.includes(address?.toLowerCase());

    if (!isConnected) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-12 text-center">
                <MessageCircle className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-white mb-2">Connect your wallet</h1>
                <p className="text-white/50">To access team chat</p>
            </div>
        );
    }

    if (!isMember && team) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-12 text-center">
                <MessageCircle className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-white mb-2">Access denied</h1>
                <p className="text-white/50 mb-4">You are not a member of this team</p>
                <Link to="/teams"><Button variant="outline">View teams</Button></Link>
            </div>
        );
    }

    const groupedMessages = messages.reduce((groups, message) => {
        const date = message.created_at ? message.created_at.split('T')[0] : 'unknown';
        if (!groups[date]) groups[date] = [];
        groups[date].push(message);
        return groups;
    }, {});

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="team-chat-page">
            <div className="flex items-center gap-4 mb-6">
                <Link to="/teams" className="text-white/50 hover:text-white"><ChevronLeft className="w-5 h-5" /></Link>
                <div className="flex items-center gap-3 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9B6FE8]/20 to-[#6F3FCA]/20 flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-[#9B6FE8]" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-white">{team?.name || 'Team Chat'}</h1>
                        <p className="text-white/50 text-sm">
                            {team?.members?.length || 0} members - {onlineMembers.length} online
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1.5" data-testid="ws-status">
                    {wsConnected ? (
                        <><Wifi className="w-4 h-4 text-[#00FF66]" /><span className="text-xs text-[#00FF66]">Live</span></>
                    ) : (
                        <><WifiOff className="w-4 h-4 text-white/30" /><span className="text-xs text-white/30">Offline</span></>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-220px)]">
                <div className="lg:col-span-3 flex flex-col">
                    <Card className="bg-white/[0.02] border-white/5 flex-1 flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 340px)' }}>
                            {loading ? (
                                <div className="space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-16 bg-white/5" />)}</div>
                            ) : messages.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center">
                                    <MessageCircle className="w-12 h-12 text-white/20 mb-4" />
                                    <p className="text-white/50">No messages yet</p>
                                    <p className="text-white/30 text-sm">Start the conversation!</p>
                                </div>
                            ) : (
                                <>
                                    {Object.entries(groupedMessages).map(([date, msgs]) => (
                                        <div key={date}>
                                            <DateSeparator date={date} />
                                            {msgs.map((msg, idx) => {
                                                const isOwn = msg.sender === address?.toLowerCase() || msg.is_mine;
                                                const showSender = idx === 0 || msgs[idx - 1]?.sender !== msg.sender;
                                                return <ChatMessage key={msg.id} message={msg} isOwn={isOwn} showSender={showSender} />;
                                            })}
                                        </div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </>
                            )}
                        </div>

                        {typingUser && (
                            <div className="px-4 py-1 text-xs text-white/40">
                                {formatAddress(typingUser)} is typing...
                            </div>
                        )}

                        <form onSubmit={handleSendMessage} className="p-4 border-t border-white/5">
                            <div className="flex gap-2">
                                <Input
                                    value={newMessage}
                                    onChange={e => { setNewMessage(e.target.value); handleTyping(); }}
                                    placeholder="Write a message..."
                                    className="flex-1 bg-white/5 border-white/10"
                                    maxLength={1000}
                                    data-testid="chat-input"
                                />
                                <Button type="submit" className="bg-[#00FF66] text-[#0A0A0C]" disabled={!newMessage.trim() || sending} data-testid="send-message-btn">
                                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </Button>
                            </div>
                        </form>
                    </Card>
                </div>

                <div className="hidden lg:block">
                    <Card className="bg-white/[0.02] border-white/5 h-full">
                        <CardContent className="p-4">
                            <OnlineMembers members={onlineMembers} teamMembers={team?.members || []} captain={team?.captain} />
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
